import * as rt from "@tanstack/react-table";
import * as bp from "@blueprintjs/core";
import * as bps from '@blueprintjs/select';
import {FilterFnOption, flexRender} from "@tanstack/react-table";
import React from "react";
import {DateTime} from "luxon";
import {ActionsBar, ExportFn} from "./ActionsBar";

export const DateRangeFilter: FilterFnOption<any> = (row, columnId, filterValue) => {
    if (!filterValue)
        return true;
    const [fromDate, toDate] = filterValue as [Date, Date];
    const from = DateTime.fromJSDate(fromDate);
    const to = DateTime.fromJSDate(toDate);
    const value = row.getValue(columnId) as string;
    const dateTime = DateTime.fromISO(value);
    return from <= dateTime && dateTime <= to;
};

// Like equalsString, but also supports an array of string filter values (OR semantics)
export const EqualsStringOrInArray: FilterFnOption<any> = (row, columnId, filterValue) => {
    if (filterValue == null) return true;
    const cell = row.getValue(columnId);
    if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) return true;
        return filterValue.includes(String(cell));
    }
    return String(cell) === String(filterValue);
};

function StringEqualsMultiSelectFilter({
    column,
    table,
}: {
    column: rt.Column<any, unknown>
    table: rt.Table<any>
}) {
    const onlyUnique = (value: string, index: number, array: string[]) => {
        return array.indexOf(value) === index;
    };
    const items = table.getPreFilteredRowModel()
        .flatRows.map(row => row.getValue(column.id) as string)
        .filter(onlyUnique)
        .sort();

    // Ensure the column's filter function can handle multiple selections
    React.useEffect(() => {
        // Some versions expose setFilterFn on the column instance; guard in case it's missing
        // @ts-ignore – tanstack types may not expose setFilterFn, but it's available at runtime
        if (typeof (column as any).setFilterFn === 'function' && column.getFilterFn() !== EqualsStringOrInArray) {
            // @ts-ignore
            (column as any).setFilterFn(EqualsStringOrInArray);
        }
        // Depend on stable id to avoid re-running when column object identity changes
    }, [column.id]);

    const columnFilterValue = column.getFilterValue();
    const selected: string[] = React.useMemo(() => {
        if (columnFilterValue == null) return [];
        return Array.isArray(columnFilterValue)
            ? (columnFilterValue as string[])
            : [String(columnFilterValue)];
    }, [columnFilterValue]);

    const toggleItem = (item: string) => {
        const exists = selected.includes(item);
        const next = exists ? selected.filter(i => i !== item) : [...selected, item];
        column.setFilterValue(next.length ? next : undefined);
    };

    return (
        <bps.MultiSelect2<string>
            items={items}
            selectedItems={selected}
            itemPredicate={(query, item) => item.toLowerCase().includes(query.toLowerCase())}
            itemRenderer={(item, {handleClick, handleFocus, modifiers, query}) => {
                if (!modifiers.matchesPredicate) return null;
                if (query && !item.toLowerCase().includes(query.toLowerCase())) return null;
                const isSelected = selected.includes(item);
                return (
                    <bp.MenuItem
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        key={item}
                        onClick={handleClick}
                        onFocus={handleFocus}
                        roleStructure="listoption"
                        text={item}
                        icon={isSelected ? 'tick' : 'blank'}
                    />
                );
            }}
            onItemSelect={toggleItem}
            tagRenderer={(item) => item}
            tagInputProps={{
                placeholder: selected.length ? undefined : 'Select…',
                onRemove: (_tag, index) => {
                    const next = selected.filter((_, i) => i !== index);
                    column.setFilterValue(next.length ? next : undefined);
                }
            }}
            popoverProps={{minimal: true}}
            resetOnSelect={false}
            fill
        />
    );
}

function Filter({
                    column,
                    table,
                }: {
    column: rt.Column<any, unknown>
    table: rt.Table<any>
}) {
    const firstValue = table
        .getPreFilteredRowModel()
        .flatRows[0]?.getValue(column.id);

    const columnFilterValue = column.getFilterValue();

    // Local state to drive native date inputs for date range filtering
    const [dateRangeStart, setDateRangeStart] = React.useState<string>('');
    const [dateRangeEnd, setDateRangeEnd] = React.useState<string>('');

    // Sync local inputs from table filter value when it changes externally
    React.useEffect(() => {
        const val = columnFilterValue as [Date, Date] | undefined;
        if (val && val[0] && val[1]) {
            setDateRangeStart(DateTime.fromJSDate(val[0]).toISODate() ?? '');
            setDateRangeEnd(DateTime.fromJSDate(val[1]).toISODate() ?? '');
        } else {
            setDateRangeStart('');
            setDateRangeEnd('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnFilterValue]);

    const sortedUniqueValues = React.useMemo(
        () =>
            typeof firstValue === 'number'
                ? []
                : Array.from(column.getFacetedUniqueValues().keys()).sort(),
        [firstValue, column]
    );

    const timestampRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z$/;
    if (`${firstValue}`.match(timestampRegex) || column.id === 'created') {
        const applyIfBoth = (startISO: string, endISO: string) => {
            if (startISO && endISO) {
                const from = DateTime.fromISO(startISO).startOf('day').toJSDate();
                const to = DateTime.fromISO(endISO).endOf('day').toJSDate();
                column.setFilterValue([from, to]);
            } else {
                column.setFilterValue(undefined);
            }
        };

        return (
            <div className="flex items-center space-x-2">
                <input
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => {
                        const v = e.target.value;
                        setDateRangeStart(v);
                        applyIfBoth(v, dateRangeEnd);
                    }}
                />
                <span>–</span>
                <input
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => {
                        const v = e.target.value;
                        setDateRangeEnd(v);
                        applyIfBoth(dateRangeStart, v);
                    }}
                />
            </div>
        );
    }

    if (
        typeof firstValue !== 'number' &&
        (column.getFilterFn() === rt.filterFns.equalsString || column.getFilterFn() === EqualsStringOrInArray)
    ) {
        return <StringEqualsMultiSelectFilter column={column} table={table}/>;
    }

    return typeof firstValue === 'number' ? (
        <div>
            <div className="flex space-x-2">
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    value={(columnFilterValue as [number, number])?.[0] ?? ''}
                    onChange={value =>
                        column.setFilterValue((old: [number, number]) => [value, old?.[1]])
                    }
                    placeholder={`Min ${
                        column.getFacetedMinMaxValues()?.[0]
                            ? `(${column.getFacetedMinMaxValues()?.[0]})`
                            : ''
                    }`}
                    className="w-24 border shadow rounded"
                />
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    value={(columnFilterValue as [number, number])?.[1] ?? ''}
                    onChange={value =>
                        column.setFilterValue((old: [number, number]) => [old?.[0], value])
                    }
                    placeholder={`Max ${
                        column.getFacetedMinMaxValues()?.[1]
                            ? `(${column.getFacetedMinMaxValues()?.[1]})`
                            : ''
                    }`}
                    className="w-24 border shadow rounded"
                />
            </div>
            <div className="h-1"/>
        </div>
    ) : (
        <>
            <datalist id={column.id + 'list'}>
                {sortedUniqueValues.slice(0, 5000).map((value: any) => (
                    <option value={value} key={value}/>
                ))}
            </datalist>
            <DebouncedInput
                type="text"
                value={(columnFilterValue ?? '') as string}
                onChange={value => column.setFilterValue(value)}
                placeholder="Suche..."
                className="w-36 border shadow rounded"
                list={column.id + 'list'}
            />
            <div className="h-1"/>
        </>
    )
}

// A debounced input react component
function DebouncedInput({
                            value: initialValue,
                            onChange,
                            debounce = 500,
                            ...props
                        }: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        // Only trigger onChange if the value is different from the prop-provided value
        const timeout = setTimeout(() => {
            if (value !== initialValue) {
                onChange(value)
            }
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, debounce, onChange, initialValue])

    return (
        <input {...props} value={value} onChange={e => setValue(e.target.value)}/>
    )
}

export type AppTableProps<T> = {
    table: rt.Table<T>,
    exportFns?: ExportFn[], // FIXME no loading state or errors
    create?: () => void,
    openAll?: () => void,
    // Bulk selection actions
    selectedCount?: number,
    onBulkArchive?: () => void,
    onBulkUnarchive?: () => void,
    // Pagination (client-side) — when enabled, uses tanstack pagination state from the passed table
    showPagination?: boolean,
    pageSizeOptions?: number[]
};

export function AppTable<T>({table, exportFns, create, openAll, selectedCount, onBulkArchive, onBulkUnarchive, showPagination, pageSizeOptions}: AppTableProps<T>) {
    const options = pageSizeOptions && pageSizeOptions.length ? pageSizeOptions : [10, 25, 50, 100];
    return <>
        <ActionsBar results={table.getFilteredRowModel().flatRows.length}
                    exports={exportFns}
                    create={create}
                    reset={table.getState().columnFilters.length > 0 ? () => table.setColumnFilters([]) : undefined}
                    openAll={openAll}
                    selectedCount={selectedCount}
                    bulkArchive={onBulkArchive}
                    bulkUnarchive={onBulkUnarchive}/>
        <section>
            <bp.Card elevation={bp.Elevation.TWO}>
                <bp.HTMLTable striped bordered style={{width: '100%'}}>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : (<div>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {header.column.getCanSort() &&
                                                <span style={{cursor: 'pointer'}}
                                                      onClick={header.column.getToggleSortingHandler()}>
                                        {' '}
                                                    <small><bp.Icon
                                                        icon={(header.column.getIsSorted() ? `sort-${header.column.getIsSorted()}` : 'sort') as bp.IconName}/></small>
                                    </span>}
                                        </div>)}
                                    {!header.isPlaceholder && header.column.getCanFilter() ? (
                                        <div>
                                            <Filter column={header.column} table={table}/>
                                        </div>
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </bp.HTMLTable>
                {showPagination && (
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0'}}>
                        <div>
                            <bp.ButtonGroup minimal>
                                <bp.Button icon="double-chevron-left" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} />
                                <bp.Button icon="chevron-left" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} />
                                <bp.Button icon="chevron-right" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} />
                                <bp.Button icon="double-chevron-right" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} />
                            </bp.ButtonGroup>
                            <span style={{marginLeft: 12}}>
                                Page {table.getState().pagination.pageIndex + 1} of {Math.max(1, table.getPageCount())}
                            </span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span>Rows per page:</span>
                            <bp.HTMLSelect
                                value={table.getState().pagination.pageSize}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => table.setPageSize(Number(e.target.value))}
                            >
                                {options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </bp.HTMLSelect>
                        </div>
                    </div>
                )}
            </bp.Card>
        </section>
    </>;
}

export function SlicedCell({text, max}: { text: string, max: number }) {
    const slicedText = text.length > max ? text.slice(0, max) + '...' : text;
    return <span>{slicedText}</span>;
}

export function ColorCell({color}: { color: string }) {
    return <span style={{border: `2px solid ${color}`, padding: '3px'}}>
        {color}
    </span>;
}

export type RowActionProps = {
    edit?: () => void,
    delete?: () => void
};

export function RowActions(props: RowActionProps) {
    return <>
        {props.edit && <bp.AnchorButton onClick={props.edit} icon="edit" intent={bp.Intent.PRIMARY} outlined/>}
        {props.delete && <bp.AnchorButton onClick={props.delete} icon="trash" intent={bp.Intent.DANGER} outlined/>}
    </>;
}